import React, { useState, useEffect } from 'react';
import { Button, TextField, Typography, Box, Stack, Divider, CircularProgress, Alert, Link } from '@mui/material';
import Modal from 'components/_layouts/Modal';
import { translate as t } from 'hooks/useTranslations';
import useStepUp from '../_data_sources/useStepUp';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useAuth } from 'react-oidc-context';
import _ from 'lodash';
import { getEnv } from 'data_sources/constants';
import { secureStorage } from 'utils/secureStorage';

interface StepUpProps {
  organizationId: string;
  organizationName: string;
  open: boolean;
  onClose: () => void;
}

const StepUp: React.FC<StepUpProps> = ({ organizationId, organizationName, open, onClose }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [otpCode, setOtpCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [setupError, setSetupError] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [qrCodeString, setQrCodeString] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMfaVerified, setIsMfaVerified] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tokenVerified, setTokenVerified] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const stepUp = useStepUp();

  useEffect(() => {
    if (open) {
      if (!auth.isAuthenticated) {
        setSetupError(t('organizations.stepUp.notAuthenticated'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setSetupError('');

      stepUp.initiateAuth(
        organizationId,
        {},
        (response) => {
          setIsLoading(false);
          setIsMfaVerified(response.verified);
        },
        (errors) => {
          console.error('Initiate auth error:', errors);
          setSetupError(t('organizations.stepUp.setupFailed'));
          setIsLoading(false);
        }
      );
    }
  }, [open, organizationId, auth.isAuthenticated, t]);

  const handleVerify = async () => {
    if (!auth.isAuthenticated) {
      setVerifyError(t('organizations.stepUp.notAuthenticated'));
      return;
    }

    if (!otpCode) {
      setVerifyError(t('organizations.stepUp.verifyError'));
      return;
    }

    setIsVerifying(true);
    try {
      // Use the existing respond to challenge API
      await stepUp.respondToChallenge(
        organizationId,
        { code: otpCode },
        (response) => {
          if (response.status?.toUpperCase() === 'COMPLETED') {
            // Store organization access with 8-hour expiration
            const expirationDate = new Date(Date.now() + 8 * 60 * 60 * 1000);
            const existingAccess = secureStorage.getItem('step-up');
            const accessMap = existingAccess ? JSON.parse(existingAccess) : {};
            accessMap[organizationId] = expirationDate.toISOString();
            secureStorage.setItem('step-up', JSON.stringify(accessMap));
            navigate(`/admin/switch-org/${organizationId}?redir=${encodeURIComponent('/admin/lexid/settings')}`);
            onClose();
          } else {
            setVerifyError(t('organizations.stepUp.verifyFailed'));
          }
        },
        (errors) => {
          console.error('MFA verification failed', errors);
          setVerifyError(t('organizations.stepUp.verifyFailed'));
        }
      );
    } catch (error) {
      console.error('MFA verification failed', error);
      setVerifyError(t('organizations.stepUp.verifyFailed'));
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Modal
      title={t('organizations.stepUp.title', 'Request Access to %{organizationName}', { organizationName })}
      size='sm'
      actions={
        <>
          <Button
            color='neutral'
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
        </>
      }
      open={open}
      onClose={onClose}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 3,
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {setupError && (
              <Box sx={{ p: 3 }}>
                <Alert severity='error'>{setupError}</Alert>
              </Box>
            )}

            {isMfaVerified ? (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography
                    variant='h6'
                    gutterBottom
                  >
                    {t('organizations.stepUp.verifyTitle')}
                  </Typography>
                  {isLoading && (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  )}
                </Box>
                <TextField
                  fullWidth
                  label={false}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleVerify();
                    }
                  }}
                  error={!!verifyError}
                  helperText={verifyError || t('organizations.stepUp.verifyHint')}
                  disabled={!isMfaVerified}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant='contained'
                  onClick={handleVerify}
                  fullWidth
                  disabled={isVerifying || !isMfaVerified || !auth.isAuthenticated}
                  endIcon={
                    isVerifying ? (
                      <CircularProgress
                        size={20}
                        color='inherit'
                      />
                    ) : null
                  }
                >
                  {t('organizations.stepUp.verify')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant='body1'>
                  If you haven't set up MFA,{' '}
                  <Link
                    href='/admin/mfa-settings'
                    underline='hover'
                  >
                    click here
                  </Link>{' '}
                  to do so.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default StepUp;
